import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { ForecastedUsageApiResponse, Graph, GraphPair, TenantUsageData } from 'src/app/app.types';
import { UsageAmountsService } from 'src/app/services/usage/usage-amounts.service';
import { convertDateToMonth, convertMonthKeyToDate } from 'src/util/conversion-helpers';

type DataShownOption = 'video' | 'audio';

@Component({
    selector: 'redaction-graph',
    templateUrl: './redaction-graph.component.html',
    styleUrls: ['./redaction-graph.component.scss'],
    standalone: false
})
export class RedactionGraphComponent implements OnInit, OnDestroy {
  @Input() tenantId?: string;

  endOfFirstRetention: Date | null = null;

  earliestSelectionDate: Date = new Date(2024, 0, 1);
  latestSelectionDate: Date = new Date();
  titles: { [key in DataShownOption]: string } = {
    video: 'Video Reaction requests over time',
    audio: 'Audio Reaction requests over time'
  };

  dataShown: DataShownOption = 'video';

  videoRedactionGraph: Graph = { description: 'Video Redaction Requests', type: 'bar', color: '#1A81E0', data: [], labelType: 'normal' };
  audioRedactionGraph: Graph = { description: 'Audio Redaction Requests', type: 'bar', color: '#1A81E0', data: [], labelType: 'normal' };
  
  public get currentTitle() : string {
    return this.titles[this.dataShown];
  }

  @Input()
  public set selectedDate(v : Date) {
    console.log('Selected date set:', v);
    this.graphPair.startDate = v;
    this.refreshDataForStartDate$.next(v);
  }
  
  public get selectedDate() : Date | undefined {
    return this.graphPair.startDate;
  }
  
  @Input()
  public set initialTenantData(v : TenantUsageData) {
    console.log('Initial tenant data set:', v);
    if(v) {
      this.endOfFirstRetention = v.endOfFirstRetention;
      this.graphPair.startDate = Object.keys(v.usage).length > 0 ? convertMonthKeyToDate(Object.keys(v.usage)[0]) : this.graphPair.startDate;
      this.earliestSelectionDate = this.graphPair.startDate;
      if(v.customer?.subscriptionStartDate && new Date(v.customer?.subscriptionStartDate).getTime() < this.earliestSelectionDate.getTime()) {
        const subStartDate = new Date(v.customer.subscriptionStartDate);
        this.earliestSelectionDate = new Date(subStartDate.getFullYear(), subStartDate.getMonth(), 1);
      }
      this.setDataPointsFromUsageData(v.usage);
    }
  }

  graphPair: GraphPair = {
    leftGraph: { description: 'Video Redaction Requests', type: 'bar', color: '#1A81E0', data: [], labelType: 'normal' },
    rightGraph: { description: null, type: 'line', color: '#39D6D6', data: [], labelType: 'normal' },
    startDate: new Date(new Date().getFullYear()-1, new Date().getMonth() + 1, 1)
  }
  
  refreshDataForTenant$ = new Subject<string>();
  refreshDataForStartDate$ = new Subject<Date>();
  unSubscribe$ = new Subject();

  constructor(private readonly usageService: UsageAmountsService) {}

  ngOnDestroy(): void {
    this.unSubscribe$.next(null);
    this.unSubscribe$.complete();
  }

  ngOnInit(): void {
      this.refreshDataForStartDate$.pipe(
        filter(() => !!this.tenantId),
        switchMap(startDate => this.usageService.getForecast(
          this.tenantId, startDate.toISOString(), 12, this.endOfFirstRetention
        )),
        takeUntil(this.unSubscribe$)
      ).subscribe(usageData => {
          console.log(`Usage data for ${this.tenantId} :`, usageData);
          this.setDataPointsFromUsageData(usageData);
      });
  }

  onDateChange(selectedDate: Date) {
    this.selectedDate = selectedDate;
  }

  setDataShown(value: DataShownOption | null = null) {
    if(value) {this.dataShown = value;}
    
    if(this.dataShown === 'video'){
      this.graphPair = { ...this.graphPair, ...{
        leftGraph: {...this.videoRedactionGraph},
        startDate: this.graphPair.startDate
      }}
    } else {
      this.graphPair = { ...this.graphPair, ...{
        leftGraph: {...this.audioRedactionGraph},
        startDate: this.graphPair.startDate
      }}
    }
  }

  setDataPointsFromUsageData(usageData: ForecastedUsageApiResponse) {
      this.videoRedactionGraph.data = Object.entries(usageData).map(u => { 
        return {
          name: convertDateToMonth(convertMonthKeyToDate(u[0])), actual: u[1].actualUsage?.redactionRequestsLastHourCount
        }
      });
      
      this.audioRedactionGraph.data = Object.entries(usageData)
        .filter(u => u[1].actualUsage?.audioRedactionRequestsInTimeFrame).map(u => { 
        return {
          name: convertDateToMonth(convertMonthKeyToDate(u[0])), 
          actual: u[1].actualUsage?.audioRedactionRequestsInTimeFrame ?? undefined
        }
      });

      this.setDataShown();
  }
}
